import React, { useState } from 'react';
import api from '../../utils/api';
import {
  HiExclamationCircle,
  HiCheckCircle,
  HiXCircle,
  HiOutlineCheckCircle,
  HiOutlineCalendar,
  HiOutlineCreditCard,
  HiOutlineSparkles,
} from 'react-icons/hi2';

const ViewSubscriptionDrawer = ({ subscription: initial, onClose }) => {
  const [subscription, setSubscription] = useState(initial);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newHvCode, setNewHvCode] = useState(initial.hv_code || '');
  const [manualStatus, setManualStatus] = useState('');
  const [manualReason, setManualReason] = useState('');

  const statusStyles = {
    pendiente: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-900',
      border: 'border-yellow-200',
      icon: <HiExclamationCircle className="h-6 w-6 text-yellow-500" />,
    },
    pagado: {
      bg: 'bg-green-50',
      text: 'text-green-900',
      border: 'border-green-200',
      icon: <HiCheckCircle className="h-6 w-6 text-green-500" />,
    },
    cancelado: {
      bg: 'bg-red-50',
      text: 'text-red-900',
      border: 'border-red-200',
      icon: <HiXCircle className="h-6 w-6 text-red-500" />,
    },
  };

  const style = statusStyles[subscription.order_status] || {
    bg: 'bg-gray-50',
    text: 'text-gray-800',
    border: 'border-gray-200',
    icon: <HiExclamationCircle className="h-6 w-6 text-gray-500" />,
  };

  const formatDate = (str) => {
    if (!str) return '—';
    const d = new Date(str);
    const months = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
    return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
  };

  const generateHvCode = () => {
    const parts = subscription.user_name.split(' ');
    const first = parts[0] || '';
    const initials = parts.slice(1).map(p => p[0]).join('');
    setNewHvCode(`HV${first}${initials}`.slice(0, 15));
  };

  const handleUpdate = async (data, successMessage = '') => {
    setLoading(true);
    try {
      await api.put(`/api/subscriptions/${subscription.id}`, data);
      setSubscription(prev => ({ ...prev, ...data }));
      setModal(null);
    } catch (err) {
      console.error('Error al actualizar:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end bg-black/50 font-[Manrope]">
      <div className="w-full max-w-6xl bg-white h-full flex flex-col shadow-xl">

        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            Suscripción #{subscription.id}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-4 gap-6">

          {/* Columna izquierda */}
          <div className="space-y-6 col-span-1">
            <h3 className="text-sm font-medium text-gray-700">Acciones rápidas</h3>
            <div className="space-y-2">
              <button
                onClick={() => setModal({ type: 'estado', title: 'Cambiar estado manual' })}
                className="w-full text-left text-sm rounded border px-3 py-2 hover:bg-gray-100 text-gray-700"
              >
                Establecer estado manualmente
              </button>
              <button
                onClick={() => setModal({ type: 'hv', title: 'Cambiar Código HV' })}
                className="w-full text-left text-sm rounded border px-3 py-2 hover:bg-gray-100 text-gray-700"
              >
                Cambiar código HV
              </button>
            </div>

            <hr className="border-gray-300" />

            <div className="bg-gray-50 border rounded-lg p-4 text-sm space-y-1">
              <p className="text-gray-500 font-medium">A nombre de</p>
              <p className="text-gray-800">{subscription.user_name}</p>
              <p className="text-gray-600 text-xs">{subscription.email}</p>
              <p className="pt-2 text-gray-500 font-medium">Código HV</p>
              <p className="text-gray-800">{subscription.hv_code || '—'}</p>
            </div>
          </div>

          {/* Columna derecha */}
          <div className="space-y-6 col-span-1 md:col-span-3">

            {/* Alert */}
            {subscription.order_status && (
              <div className={`rounded-lg p-4 text-sm border flex items-start gap-3 ${style.bg} ${style.text} ${style.border}`}>
                <div className="pt-0.5">{style.icon}</div>
                <div>
                  <p className="font-medium">
                    Estado de factura: <span className="capitalize">{subscription.order_status}</span>
                  </p>
                  {subscription.order_paid_at && (
                    <p className="text-xs text-gray-600">
                      Pagado el {formatDate(subscription.order_paid_at)} a las{' '}
                      {new Date(subscription.order_paid_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                ['Estado', subscription.status],
                ['Servicio', subscription.service_name],
                ['Plan', subscription.plan_name],
                ['Próximo cobro', subscription.next_billing_date?.split('T')[0] || '—'],
                ['Pedido', subscription.order_id ? `#${subscription.order_id}` : '—'],
              ].map(([label, value]) => (
                <div key={label} className="rounded border bg-white p-4">
                  <dt className="text-sm text-gray-500">{label}</dt>
                  <dd className="text-lg font-semibold text-gray-900">{value}</dd>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">Cronología</h3>
              <ol className="relative border-l border-gray-200 space-y-6">
                <li className="ml-4 flex items-start gap-3">
                  <HiOutlineCheckCircle className="text-green-600 w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">Renovación registrada</p>
                    <p className="text-sm text-gray-600">{formatDate(subscription.last_renewed_at)}</p>
                  </div>
                </li>
                <li className="ml-4 flex items-start gap-3">
                  <HiOutlineCalendar className="text-indigo-600 w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">Próxima renovación</p>
                    <p className="text-sm text-gray-600">{formatDate(subscription.next_billing_date)}</p>
                  </div>
                </li>
                <li className="ml-4 flex items-start gap-3">
                  <HiOutlineCreditCard className="text-purple-600 w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">Factura generada</p>
                    <p className="text-sm text-gray-600">{formatDate(subscription.order_date)}</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-white sticky bottom-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Cerrar
          </button>
        </div>

        {/* Modal */}
        {modal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-xl space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">{modal.title}</h3>

              {modal.type === 'hv' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newHvCode}
                    onChange={(e) => setNewHvCode(e.target.value)}
                    className="w-full border px-3 py-2 rounded text-sm"
                  />
                  <button
                    onClick={generateHvCode}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                  >
                    <HiOutlineSparkles className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <select
                    value={manualStatus}
                    onChange={(e) => setManualStatus(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar estado</option>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="pending">Pendiente</option>
                  </select>
                  <textarea
                    placeholder="Razón"
                    value={manualReason}
                    onChange={(e) => setManualReason(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setModal(null)} className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200">Cancelar</button>
                <button
                  onClick={() => {
                    if (modal.type === 'hv') {
                      handleUpdate({ hv_code: newHvCode.trim() });
                    } else {
                      handleUpdate({ status: manualStatus });
                    }
                  }}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewSubscriptionDrawer;
